import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/Button';
import { motion } from 'framer-motion';

const Hero = () => {
  const { t } = useLanguage();
  
  const heroImage = "https://www.cpdanmark.dk/media/1230/kasper_juni16_5446.jpg?crop=0.0000000000000002526374171591,0.01333809693938311,0,0.47245956778066056&cropmode=percentage&width=1284&height=440&rnd=134096775550000000&quality=80";

  return (
    <section className="relative pt-24 pb-16 md:pt-40 md:pb-24 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(circle_at_50%_20%,rgba(217,119,6,0.05),transparent_50%)] pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="flex flex-col gap-10">
          
          {/* Headline Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-brand-textHeading tracking-tight text-balance leading-[1.1]">
              {t.hero.headline}
            </h1>
          </motion.div>

          {/* Image Section - Optimized for wide 3:1 aspect ratio */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative w-full"
          >
            <div className="relative rounded-2xl overflow-hidden border border-brand-border shadow-2xl aspect-[1284/440] bg-brand-card">
              <img 
                src={heroImage} 
                alt="Kasper Jensen - Konsulentbixen" 
                className="w-full h-full object-cover grayscale-[15%] hover:grayscale-0 transition-all duration-1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/30 via-transparent to-transparent pointer-events-none"></div>
            </div>
            
            <div className="mt-3 flex justify-between items-center px-1">
              <div className="hidden md:flex gap-4 text-[10px] uppercase tracking-widest text-brand-textMuted font-semibold">
                <span>Journalistik</span>
                <span>Affiliate</span>
                <span>Strategi</span>
              </div>
              <p className="text-[10px] uppercase tracking-widest text-brand-textMuted ml-auto">
                Foto: <a 
                  href="https://hellemoos.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-brand-accent transition-colors border-b border-brand-border/50 hover:border-brand-accent"
                >
                  Helle Moos
                </a>
              </p>
            </div>
          </motion.div>

          {/* Bottom Content Section */}
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <motion.div 
              className="lg:col-span-7"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <p className="text-xl md:text-2xl text-brand-textMuted mb-6 font-medium leading-relaxed">
                {t.hero.subheadline}
              </p>
              <p className="text-base md:text-lg text-brand-text leading-relaxed opacity-90 max-w-2xl">
                {t.hero.intro}
              </p>
            </motion.div>

            <motion.div 
              className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col xl:flex-row items-stretch sm:items-center lg:items-stretch xl:items-center gap-4 lg:justify-end"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <Button href="#contact" variant="primary" className="whitespace-nowrap">
                {t.hero.primaryCta}
              </Button>
              <Button href="#services" variant="secondary" className="whitespace-nowrap">
                {t.hero.secondaryCta}
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Divider & Trustline */}
        <motion.div 
          className="mt-16 pt-8 border-t border-brand-border/50 flex flex-wrap justify-center lg:justify-start gap-4 text-[10px] md:text-xs font-bold tracking-[0.2em] uppercase text-brand-textMuted/70"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.8 }}
        >
          {t.hero.trustLine.split('. ').map((word, i) => (
            <React.Fragment key={i}>
              <span>{word.replace('.', '')}</span>
              {i < t.hero.trustLine.split('. ').length - 1 && <span className="text-brand-accent/30">•</span>}
            </React.Fragment>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;